// The reference lists the plan builder picks from: the dishes and the units
// they are measured in. The plan types a new plan is created under come from
// `/api/v1/meal-plan-types/` now, so they are no longer here.
//
// All three are stand-ins, the same way `clientFormOptions` was before the
// catalog endpoints landed — a dish catalog is a backend list, not a constant.
// The macros are per 100 g so any unit can be scaled off them; see `dishMacros`.
//
// Nothing here imports from the rest of the module, which is what keeps
// `planMapping` — the one thing that reads these — free of a cycle.
//
// What is left of their job is narrow. Every row the builder writes now
// carries the macros and the unit label the food API answered with, so these
// are only reached by a row that has neither: a plan written before those
// endpoints landed, or a test fixture. They go the day nothing in the running
// app can produce such a row.

import type { CatalogDish, MealUnit } from "../types/plans.types";

/**
 * What a dish can be measured in, and what one of it weighs.
 *
 * Gram is first because it is the one that needs no judgement; the household
 * measures after it are the ones a client actually serves with.
 */
export const MEAL_UNITS: MealUnit[] = [
  { name: "Gram", grams: 1 },
  { name: "Millilitre", grams: 1 },
  { name: "Katori", grams: 150 },
  { name: "Bowl", grams: 200 },
  { name: "Plate", grams: 250 },
  { name: "Piece", grams: 40 },
  { name: "Glass", grams: 240 },
  { name: "Tablespoon", grams: 15 },
  { name: "Teaspoon", grams: 5 },
];

/** Every dish the Name column can be set to, with its macros per 100 g. */
export const CATALOG_DISHES: CatalogDish[] = [
  { name: "Chapati", per100g: { calories: 297, carbs: 46, protein: 11, fat: 7 } },
  { name: "Brown rice", per100g: { calories: 123, carbs: 26, protein: 3, fat: 1 } },
  { name: "White rice", per100g: { calories: 130, carbs: 28, protein: 3, fat: 0 } },
  { name: "Dal tadka", per100g: { calories: 116, carbs: 13, protein: 6, fat: 4 } },
  { name: "Rajma curry", per100g: { calories: 140, carbs: 18, protein: 7, fat: 4 } },
  { name: "Chole", per100g: { calories: 164, carbs: 27, protein: 9, fat: 3 } },
  { name: "Paneer bhurji", per100g: { calories: 220, carbs: 6, protein: 14, fat: 16 } },
  { name: "Palak paneer", per100g: { calories: 180, carbs: 8, protein: 9, fat: 12 } },
  { name: "Mixed vegetable sabzi", per100g: { calories: 90, carbs: 10, protein: 3, fat: 4 } },
  { name: "Grilled chicken breast", per100g: { calories: 165, carbs: 0, protein: 31, fat: 4 } },
  { name: "Chicken curry", per100g: { calories: 190, carbs: 6, protein: 15, fat: 12 } },
  { name: "Grilled fish", per100g: { calories: 145, carbs: 0, protein: 22, fat: 6 } },
  { name: "Boiled egg", per100g: { calories: 155, carbs: 1, protein: 13, fat: 11 } },
  { name: "Egg white omelette", per100g: { calories: 52, carbs: 1, protein: 11, fat: 0 } },
  { name: "Idli", per100g: { calories: 130, carbs: 28, protein: 4, fat: 0 } },
  { name: "Dosa", per100g: { calories: 168, carbs: 30, protein: 4, fat: 4 } },
  { name: "Upma", per100g: { calories: 155, carbs: 23, protein: 4, fat: 5 } },
  { name: "Poha", per100g: { calories: 130, carbs: 25, protein: 3, fat: 2 } },
  { name: "Oats porridge", per100g: { calories: 71, carbs: 12, protein: 3, fat: 2 } },
  { name: "Greek yogurt", per100g: { calories: 59, carbs: 4, protein: 10, fat: 0 } },
  { name: "Curd", per100g: { calories: 61, carbs: 5, protein: 4, fat: 3 } },
  { name: "Paneer tikka", per100g: { calories: 240, carbs: 8, protein: 18, fat: 15 } },
  { name: "Sprouts salad", per100g: { calories: 100, carbs: 17, protein: 8, fat: 1 } },
  { name: "Green salad", per100g: { calories: 35, carbs: 7, protein: 2, fat: 0 } },
  { name: "Fruit bowl", per100g: { calories: 60, carbs: 14, protein: 1, fat: 0 } },
  { name: "Banana", per100g: { calories: 89, carbs: 23, protein: 1, fat: 0 } },
  { name: "Apple", per100g: { calories: 52, carbs: 14, protein: 0, fat: 0 } },
  { name: "Almonds", per100g: { calories: 579, carbs: 22, protein: 21, fat: 50 } },
  { name: "Peanut chikki", per100g: { calories: 460, carbs: 55, protein: 15, fat: 20 } },
  { name: "Buttermilk", per100g: { calories: 40, carbs: 5, protein: 3, fat: 1 } },
  { name: "Green tea", per100g: { calories: 1, carbs: 0, protein: 0, fat: 0 } },
];
